/**
 * ActiveAdventureTitleImage Component
 * 
 * Fetches an active session by session ID, extracts the adventure ID,
 * fetches the adventure header to get the title image ID,
 * then displays the title image. Used for <active-adventure> tags in rich text.
 * Clicking the image navigates to the existing session (not creates new).
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdventureTitleImage } from './AdventureTitleImage';
import apiService from '../../services/api';
import useAuthStore from '../../stores/authStore';

export interface ActiveAdventureTitleImageProps {
  /**
   * Session ID (UUID) of the active session
   */
  sessionId: string;
  
  /**
   * Size variant to display
   */
  variant?: 'thumbnail' | 'card' | 'large';
  
  /**
   * Alt text for the image
   */
  alt?: string;
  
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Component that fetches active session data and displays the adventure title image
 * Clicking navigates to the existing session
 */
export const ActiveAdventureTitleImage: React.FC<ActiveAdventureTitleImageProps> = ({
  sessionId,
  variant = 'card',
  alt,
  className = ''
}) => {
  const navigate = useNavigate();
  const [titleImageId, setTitleImageId] = useState<string | undefined>(undefined);
  const [adventureId, setAdventureId] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [playerId, setPlayerId] = useState<string | undefined>(undefined);
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchSessionData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Get current user
        const currentUser = useAuthStore.getState().user;
        if (!currentUser) {
          throw new Error('User must be logged in to view active adventures');
        }

        // Fetch session data
        const session = await apiService.getSession(sessionId);
        
        if (!isMounted) return;

        // Get players in this session to find current user's player
        const players = await apiService.getSessionPlayers(session.id);
        const currentUserPlayer = players.find(player => 
          (player as any).userId === currentUser.id
        );

        if (!currentUserPlayer) {
          throw new Error('You are not a player in this session');
        }

        setPlayerId(currentUserPlayer.id);

        // Fetch adventure header to get title image ID
        const header = await apiService.getAdventureHeader(session.adventureId);
        
        if (isMounted) {
          setAdventureId(session.adventureId);
          setTitleImageId(header.titleImageId);
          setIsLoading(false);
        }
      } catch (err) {
        console.warn(`Failed to fetch active adventure for session ${sessionId}:`, err);
        if (isMounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load active adventure';
          setError(errorMessage);
          setIsLoading(false);
        }
      }
    };

    fetchSessionData();

    return () => {
      isMounted = false;
    };
  }, [sessionId]);

  const handleClick = () => {
    if (!playerId || isNavigating) return;
    
    setIsNavigating(true);
    // Navigate to existing session (not create new)
    navigate(`/scene/${sessionId}/${playerId}`);
  };

  if (isLoading) {
    return (
      <div className={`rich-text-active-adventure-loading ${className}`} style={{
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '0.5rem',
        padding: '1rem',
        minHeight: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <span className="text-gray-400 text-sm">Loading...</span>
      </div>
    );
  }

  if (error || !titleImageId || !adventureId) {
    return (
      <div className={`rich-text-active-adventure-error ${className}`} style={{
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 0, 0, 0.3)',
        borderRadius: '0.5rem',
        padding: '1rem',
        minHeight: '64px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <span className="text-red-400 text-sm">
          {error ? `⚠️ ${error}` : '🖼️ Image not available'}
        </span>
      </div>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={isNavigating || !playerId}
      className={`rich-text-active-adventure ${className}`}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: isNavigating || !playerId ? 'not-allowed' : 'pointer',
        opacity: isNavigating ? 0.6 : 1
      }}
      aria-label={alt || `Resume active adventure: ${sessionId}`}
    >
      <AdventureTitleImage
        titleImageId={titleImageId}
        adventureId={adventureId}
        variant={variant}
        alt={alt || `Active Adventure: ${sessionId}`}
        className={className}
        responsive={true}
        lazy={false}
      />
    </button>
  );
};

export default ActiveAdventureTitleImage;

